import { useState, useCallback, useEffect, useRef } from "react";
import { X, Upload, FileText, CheckCircle, AlertCircle, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { addTrade } from "@/stores/tradeStore";
import { toast } from "sonner";
import type { StoredTrade } from "@/stores/tradeStore";

//  Format detection & parsers 

type CSVFormat = "mt4" | "mt5" | "tradovate" | "ninjatrader" | "generic" | "unknown";

// Robust money/number parser. Plain parseFloat silently corrupts values like
// "$1,234.56" (comma stops parsing → 1) or "(123.45)" (→ NaN). This strips
// currency symbols + thousands separators and treats (parens) / leading - as negative.
// Assumes US formatting (dot = decimal). EU decimal-comma exports would need locale detection.
function parseMoney(v: string | undefined | null): number {
  if (v == null) return 0;
  let s = String(v).trim();
  if (!s || s === "-") return 0;
  // Negative if wrapped in (parens), if a minus appears anywhere BEFORE the first
  // digit (covers "-580", "-$580", and "$-580" — currency symbol before the sign),
  // or a trailing minus ("580-"). The old check only caught a leading "-", so
  // "$-580" was read as +580 and losses imported as gains.
  const firstDigit = s.search(/\d/);
  const beforeDigits = firstDigit >= 0 ? s.slice(0, firstDigit) : s;
  const neg = /^\(.*\)$/.test(s) || beforeDigits.includes("-") || /-\s*$/.test(s);
  s = s.replace(/[^0-9.]/g, ""); // keep digits + decimal point only
  const n = parseFloat(s);
  if (!isFinite(n)) return 0;
  return neg ? -Math.abs(n) : n;
}

function detectFormat(headers: string[]): CSVFormat {
  const h = headers.map((x) => x.toLowerCase().trim());
  if (h.includes("ticket") && h.includes("type") && h.includes("item")) return "mt4";
  if (h.includes("deal") && h.includes("symbol") && h.includes("direction")) return "mt5";
  if (h.includes("filledqty") && h.includes("avgfillprice")) return "tradovate";
  // NinjaTrader 8 "Trades" grid export
  if (h.includes("market pos.") || (h.includes("entry price") && h.includes("exit price") && h.includes("instrument"))) return "ninjatrader";
  if (h.some((x) => x.includes("symbol") || x.includes("instrument"))) return "generic";
  return "unknown";
}

function parseCSV(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter(Boolean);
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(",").map((h) => h.replace(/^"|"$/g, "").trim());
  const rows = lines.slice(1).map((line) => {
    const vals = line.match(/(".*?"|[^,]+|(?<=,)(?=,)|(?<=,)$|^(?=,))/g) || line.split(",");
    const clean = vals.map((v) => v.replace(/^"|"$/g, "").trim());
    return Object.fromEntries(headers.map((h, i) => [h, clean[i] ?? ""]));
  });
  return { headers, rows };
}

function mt4RowToTrade(row: Record<string, string>): Partial<StoredTrade> | null {
  // MT4 History: Ticket, Open Time, Type, Size, Item, Price, S/L, T/P, Close Time, Price, Commission, Taxes, Swap, Profit
  if (!row["Item"] && !row["item"]) return null;
  const instrument = row["Item"] || row["item"] || "";
  const type = (row["Type"] || row["type"] || "").toLowerCase();
  if (type === "balance" || type === "credit") return null;
  const openTime = row["Open Time"] || row["open time"] || "";
  const dateParts = openTime.split(" ");
  const pnl = parseMoney(row["Profit"] || row["profit"]);
  const entry = parseMoney(row["Price"]);
  const exit = parseMoney(row["Price.1"] || row["Close Price"]);
  const sl = parseMoney(row["S/L"] || row["s/l"]);
  const tp = parseMoney(row["T/P"] || row["t/p"]);
  const size = parseMoney(row["Size"] || row["size"]);
  return {
    date: dateParts[0] || new Date().toISOString().split("T")[0],
    time: dateParts[1] || "00:00",
    instrument,
    direction: type.includes("sell") ? "short" : "long",
    entryPrice: entry,
    exitPrice: exit || entry,
    stopLoss: sl,
    takeProfit: tp,
    pnl,
    pnlPercent: 0,
    lotSize: size,
    rr: sl > 0 && entry > 0 ? Math.abs(exit - entry) / Math.abs(entry - sl) : 0,
    strategy: "MT4 Import",
    session: "london",
    emotion: "neutral",
    notes: `Imported from MT4 (Ticket: ${row["Ticket"] || ""})`,
    hasScreenshot: false,
    tags: ["mt4-import"],
  };
}

function mt5RowToTrade(row: Record<string, string>): Partial<StoredTrade> | null {
  // MT5 Deals: Deal, Time, Symbol, Type, Direction, Volume, Price, Order, Commission, Fee, Swap, Profit, Balance, Comment
  const symbol = row["Symbol"] || row["symbol"] || "";
  if (!symbol) return null;
  const direction = (row["Direction"] || row["direction"] || "").toLowerCase();
  if (direction === "balance" || direction === "credit") return null;
  const time = row["Time"] || row["time"] || "";
  const dateParts = time.split(" ");
  const pnl = parseMoney(row["Profit"] || row["profit"]);
  const price = parseMoney(row["Price"] || row["price"]);
  const vol = parseMoney(row["Volume"] || row["volume"]);
  return {
    date: dateParts[0] || new Date().toISOString().split("T")[0],
    time: dateParts[1] || "00:00",
    instrument: symbol,
    direction: direction.includes("sell") ? "short" : "long",
    entryPrice: price,
    exitPrice: price,
    stopLoss: 0,
    takeProfit: 0,
    pnl,
    pnlPercent: 0,
    lotSize: vol,
    rr: 0,
    strategy: "MT5 Import",
    session: "london",
    emotion: "neutral",
    notes: `Imported from MT5 (Deal: ${row["Deal"] || ""})`,
    hasScreenshot: false,
    tags: ["mt5-import"],
  };
}

function tradovateRowToTrade(row: Record<string, string>): Partial<StoredTrade> | null {
  // Tradovate: Id,AccountId,Timestamp,Action,Symbol,TradeType,Qty,Price,Commission,GainLoss
  const symbol = row["Symbol"] || row["symbol"] || "";
  if (!symbol) return null;
  const action = (row["Action"] || row["action"] || "").toLowerCase();
  if (!["buy", "sell", "buytocover", "selltoshort"].includes(action)) return null;
  const ts = row["Timestamp"] || row["timestamp"] || "";
  const d = ts ? new Date(ts) : new Date();
  const pnl = parseMoney(row["GainLoss"] || row["gainloss"] || row["Gain/Loss"]);
  const price = parseMoney(row["Price"] || row["price"] || row["AvgFillPrice"] || row["avgfillprice"]);
  const qty = parseMoney(row["Qty"] || row["qty"] || row["FilledQty"] || row["filledqty"]);
  return {
    date: d.toISOString().split("T")[0],
    time: d.toTimeString().slice(0, 5),
    instrument: symbol,
    direction: action.includes("sell") ? "short" : "long",
    entryPrice: price,
    exitPrice: price,
    stopLoss: 0,
    takeProfit: 0,
    pnl,
    pnlPercent: 0,
    lotSize: qty,
    rr: 0,
    strategy: "Tradovate Import",
    session: "new-york",
    emotion: "neutral",
    notes: `Imported from Tradovate (ID: ${row["Id"] || row["id"] || ""})`,
    hasScreenshot: false,
    tags: ["tradovate-import"],
  };
}

function ninjatraderRowToTrade(row: Record<string, string>, headers: string[]): Partial<StoredTrade> | null {
  // NinjaTrader 8 "Trades" grid export:
  // Instrument, Account, Strategy, Market pos., Qty, Entry price, Exit price,
  // Entry time, Exit time, Entry name, Exit name, Profit, Cum. net profit, Commission, ...
  const h = headers.map((x) => x.toLowerCase().trim());
  const get = (keys: string[]) => {
    for (const k of keys) {
      const idx = h.indexOf(k);
      if (idx >= 0) return row[headers[idx]] || "";
    }
    return "";
  };
  const instrument = get(["instrument"]);
  if (!instrument) return null;
  const pos = get(["market pos.", "market position", "position"]).toLowerCase();
  const entryTime = get(["entry time", "entrytime"]);
  const d = entryTime ? new Date(entryTime) : new Date();
  const valid = !isNaN(d.getTime());
  const commission = parseMoney(get(["commission"]));
  return {
    date: valid ? d.toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
    time: valid ? d.toTimeString().slice(0, 5) : "00:00",
    instrument,
    direction: pos.includes("short") ? "short" : "long",
    entryPrice: parseMoney(get(["entry price"])),
    exitPrice: parseMoney(get(["exit price"])),
    stopLoss: 0,
    takeProfit: 0,
    pnl: parseMoney(get(["profit", "realized p/l", "net profit"])),
    pnlPercent: 0,
    lotSize: parseMoney(get(["qty", "quantity"])) || 1,
    rr: 0,
    strategy: "NinjaTrader Import",
    session: "new-york",
    emotion: "neutral",
    notes: `Imported from NinjaTrader${commission ? ` (comm: ${commission})` : ""}`,
    hasScreenshot: false,
    tags: ["ninjatrader-import"],
  };
}

function genericRowToTrade(row: Record<string, string>, headers: string[]): Partial<StoredTrade> | null {
  const h = headers.map((x) => x.toLowerCase());
  const get = (keys: string[]) => {
    for (const k of keys) {
      const idx = h.indexOf(k);
      if (idx >= 0) return row[headers[idx]] || "";
    }
    return "";
  };
  const symbol = get(["symbol", "instrument", "pair", "ticker", "asset"]);
  if (!symbol) return null;
  const pnl = parseMoney(get(["pnl", "profit", "gain", "p/l", "net"]));
  const date = get(["date", "opendate", "open date", "time", "datetime"]).split(" ")[0] || new Date().toISOString().split("T")[0];
  return {
    date,
    time: "00:00",
    instrument: symbol,
    direction: (get(["direction", "type", "side"]).toLowerCase().includes("sell") ? "short" : "long"),
    entryPrice: parseMoney(get(["entry", "entryprice", "open", "openprice"])),
    exitPrice: parseMoney(get(["exit", "exitprice", "close", "closeprice"])),
    stopLoss: parseMoney(get(["sl", "stoploss", "stop"])),
    takeProfit: parseMoney(get(["tp", "takeprofit", "target"])),
    pnl,
    pnlPercent: 0,
    lotSize: parseMoney(get(["size", "qty", "quantity", "volume", "lots"])) || 1,
    rr: 0,
    strategy: "CSV Import",
    session: "london",
    emotion: "neutral",
    notes: "Imported from CSV",
    hasScreenshot: false,
    tags: ["csv-import"],
  };
}

function rowsToTrades(rows: Record<string, string>[], headers: string[], format: CSVFormat): Partial<StoredTrade>[] {
  return rows
    .map((row) => {
      if (format === "mt4") return mt4RowToTrade(row);
      if (format === "mt5") return mt5RowToTrade(row);
      if (format === "tradovate") return tradovateRowToTrade(row);
      if (format === "ninjatrader") return ninjatraderRowToTrade(row, headers);
      return genericRowToTrade(row, headers);
    })
    .filter(Boolean) as Partial<StoredTrade>[];
}

//  Component 

const FORMAT_LABELS: Record<CSVFormat, string> = {
  mt4: "MetaTrader 4",
  mt5: "MetaTrader 5",
  tradovate: "Tradovate",
  ninjatrader: "NinjaTrader 8",
  generic: "Generic CSV",
  unknown: "Unknown format",
};

export function TradeCSVImport() {
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const [format, setFormat] = useState<CSVFormat>("unknown");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [parsed, setParsed] = useState<Partial<StoredTrade>[]>([]);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = () => setOpen(true);
    document.addEventListener("open-csv-import", handler);
    return () => document.removeEventListener("open-csv-import", handler);
  }, []);

  const reset = () => {
    setFileName(""); setFormat("unknown"); setHeaders([]); setRows([]);
    setParsed([]); setDone(false);
  };

  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith(".csv") && !file.name.endsWith(".txt")) {
      toast.error("Please select a .csv file");
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { headers, rows } = parseCSV(text);
      const fmt = detectFormat(headers);
      setHeaders(headers);
      setRows(rows);
      setFormat(fmt);
      setParsed(rowsToTrades(rows, headers, fmt));
    };
    reader.readAsText(file);
  }, []);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleImport = async () => {
    setImporting(true);
    let count = 0;
    for (const t of parsed) {
      if (!t.instrument) continue;
      await addTrade({
        date: t.date || new Date().toISOString().split("T")[0],
        time: t.time || "00:00",
        instrument: t.instrument,
        direction: t.direction || "long",
        entryPrice: t.entryPrice || 0,
        exitPrice: t.exitPrice || 0,
        stopLoss: t.stopLoss || 0,
        takeProfit: t.takeProfit || 0,
        rr: t.rr || 0,
        pnl: t.pnl || 0,
        pnlPercent: t.pnlPercent || 0,
        lotSize: t.lotSize || 1,
        strategy: t.strategy || "Import",
        session: t.session || "london",
        emotion: t.emotion || "neutral",
        notes: t.notes || "",
        hasScreenshot: false,
        tags: t.tags || [],
      });
      count++;
    }
    setImporting(false);
    setDone(true);
    toast.success(`Imported ${count} trade${count !== 1 ? "s" : ""} successfully!`);
    setTimeout(() => { setOpen(false); reset(); }, 1500);
  };

  if (!open) return null;

  const previewRows = parsed.slice(0, 5);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/70" onClick={() => { setOpen(false); reset(); }} />
      <div className="relative w-full sm:max-w-2xl bg-background border border-border/60 sm:rounded-none shadow-2xl flex flex-col max-h-[92dvh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
          <div>
            <h2 className="text-sm font-semibold font-mono uppercase tracking-widest">Import Trades</h2>
            <p className="text-xs text-muted-foreground mt-0.5">MT4 · MT5 · Tradovate · NinjaTrader · Generic CSV</p>
          </div>
          <button onClick={() => { setOpen(false); reset(); }} className="p-1.5 hover:bg-secondary rounded transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Drop zone */}
          {!fileName ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className={cn(
                "border-2 border-dashed rounded-none p-10 text-center cursor-pointer transition-all",
                dragging ? "border-primary bg-primary/5" : "border-border/40 hover:border-primary/50 hover:"
              )}
            >
              <Upload className={cn("w-8 h-8 mx-auto mb-3 transition-colors", dragging ? "text-primary" : "text-muted-foreground")} />
              <p className="text-sm font-medium">Drop your CSV here or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">Supports MT4, MT5, Tradovate, NinjaTrader exports</p>
              <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 border border-border/40 bg-secondary/20">
              <FileText className="w-5 h-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{fileName}</p>
                <p className="text-xs text-muted-foreground">{FORMAT_LABELS[format]} · {rows.length} rows · {parsed.length} valid trades</p>
              </div>
              <button onClick={reset} className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 border border-border/40 hover:bg-secondary">
                Change
              </button>
            </div>
          )}

          {/* Format help */}
          {!fileName && (
            <details className="group">
              <summary className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors list-none">
                <ChevronDown className="w-3.5 h-3.5 group-open:rotate-180 transition-transform" />
                How to export from each platform
              </summary>
              <div className="mt-3 space-y-2 text-xs text-muted-foreground pl-5">
                <p><span className="text-foreground font-medium">MT4:</span> Terminal  Account History tab  right-click  Save as Report (Detailed)</p>
                <p><span className="text-foreground font-medium">MT5:</span> Toolbox  History tab  right-click  Save as Detailed Report</p>
                <p><span className="text-foreground font-medium">Tradovate:</span> Log into the Tradovate <span className="text-foreground">Web</span> app → click <span className="text-foreground">Reports</span> (right side) → select the <span className="text-foreground">Orders</span> report → choose your date range → <span className="text-foreground">Download CSV</span></p>
                <p><span className="text-foreground font-medium">NinjaTrader 8:</span> Control Center  Trade Performance  Trades tab  right-click  Export (set Display units to Currency)</p>
              </div>
            </details>
          )}

          {/* Preview table */}
          {parsed.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono mb-2">Preview (first 5)</p>
              <div className="overflow-x-auto border border-border/40">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border/40">
                      {["Date","Instrument","Dir","P/L","Lot"].map((h) => (
                        <th key={h} className="text-left px-3 py-2 font-mono text-muted-foreground uppercase tracking-wider text-[10px]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((t, i) => (
                      <tr key={i} className="border-b border-border/20 hover:bg-secondary/20">
                        <td className="px-3 py-2 font-mono">{t.date}</td>
                        <td className="px-3 py-2 font-mono">{t.instrument}</td>
                        <td className={cn("px-3 py-2 font-mono uppercase text-[10px]", t.direction === "long" ? "text-profit" : "text-loss")}>{t.direction}</td>
                        <td className={cn("px-3 py-2 font-mono", (t.pnl || 0) >= 0 ? "text-profit" : "text-loss")}>{(t.pnl || 0) >= 0 ? "+" : ""}{t.pnl?.toFixed(2)}</td>
                        <td className="px-3 py-2 font-mono text-muted-foreground">{t.lotSize}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsed.length > 5 && (
                  <p className="text-[10px] text-muted-foreground text-center py-2 border-t border-border/20">
                    +{parsed.length - 5} more trades
                  </p>
                )}
              </div>
            </div>
          )}

          {done && (
            <div className="flex items-center gap-2 p-3 border border-profit/30 text-profit text-sm">
              <CheckCircle className="w-4 h-4 shrink-0" />
              Import complete!
            </div>
          )}

          {format === "unknown" && fileName && (
            <div className="flex items-center gap-2 p-3 border border-destructive/30 text-destructive text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Could not detect format. Try mapping columns manually.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border/40 flex items-center justify-between gap-3">
          <button onClick={() => { setOpen(false); reset(); }} className="px-4 py-2 text-sm border border-border/40 text-muted-foreground hover:bg-secondary transition-colors">
            Cancel
          </button>
          {parsed.length > 0 && !done && (
            <button
              onClick={handleImport}
              disabled={importing}
              className="px-5 py-2 bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {importing ? (
                <><span className="w-3.5 h-3.5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />Importing…</>
              ) : (
                <>Import {parsed.length} Trade{parsed.length !== 1 ? "s" : ""}</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
